import { Controller } from "@hotwired/stimulus"

/**
 * Thin client-side controller for the change calendar.
 *
 * View rendering (month / week / day grids) is owned by ViewComponents
 * rendered inside a Turbo Frame. This controller only handles:
 *
 *   - current-time indicator tick (day + week views)
 *   - keyboard shortcuts (m/w/d/t/←/→)
 *   - history.replaceState after Turbo Frame nav so URLs are bookmarkable
 *   - prev/next/today buttons (navigate the Turbo Frame)
 */
export default class extends Controller {
  static targets = ["nowLine", "monthLabel"]
  static values = {
    url: String,
    view: { type: String, default: "month" },
  }

  connect() {
    this._bindKeyboard = this._onKeyDown.bind(this)
    document.addEventListener("keydown", this._bindKeyboard)
    this._startNowLineTick()
    this._updateUrl()
  }

  disconnect() {
    document.removeEventListener("keydown", this._bindKeyboard)
    this._stopNowLineTick()
  }

  // Called by the switcher links when clicked — records the selected view
  // so the URL reflects the mode the server just rendered.
  recordView(event) {
    const link = event.currentTarget
    const view = link?.dataset?.viewValue
    if (view) {
      this.viewValue = view
      this._updateUrl({ view })
    }
  }

  previousPeriod() {
    this._navigate(-1)
  }

  nextPeriod() {
    this._navigate(1)
  }

  today() {
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("view", this.viewValue)
    this._visitFrame(url.toString())
  }

  _navigate(direction) {
    const url = new URL(this.urlValue, window.location.origin)
    const params = new URLSearchParams(window.location.search)
    const year = parseInt(params.get("year") || new Date().getFullYear(), 10)
    const month0 = parseInt(params.get("month") || new Date().getMonth(), 10)

    let newYear = year
    let newMonth = month0

    if (this.viewValue === "month") {
      newMonth += direction
      if (newMonth < 0) {
        newMonth = 11
        newYear -= 1
      } else if (newMonth > 11) {
        newMonth = 0
        newYear += 1
      }
    }
    // For week/day, server derives range from current time — we just re-fetch.
    // A future enhancement can add from/to shifting here.

    url.searchParams.set("view", this.viewValue)
    url.searchParams.set("year", String(newYear))
    url.searchParams.set("month", String(newMonth))
    this._visitFrame(url.toString())
  }

  _visitFrame(href) {
    const frame = document.getElementById("calendar-grid")
    if (frame) {
      frame.src = href
    } else {
      window.location.href = href
    }
    this._updateUrl({ view: this.viewValue })
  }

  _updateUrl({ view } = {}) {
    const url = new URL(window.location.href)
    if (view) url.searchParams.set("view", view)
    window.history.replaceState(
      { view: url.searchParams.get("view") },
      "",
      url.toString(),
    )
  }

  // ─── Now-line tick ────────────────────────────────────────────────────────

  _startNowLineTick() {
    if (!this.hasNowLineTarget) return
    this._nowLineInterval = setInterval(() => this._repositionNowLine(), 60_000)
  }

  _stopNowLineTick() {
    if (this._nowLineInterval) {
      clearInterval(this._nowLineInterval)
      this._nowLineInterval = null
    }
  }

  _repositionNowLine() {
    if (!this.hasNowLineTarget) return
    const now = new Date()
    const offset = now.getHours() * 48 + (now.getMinutes() * 48) / 60
    this.nowLineTarget.style.top = `${offset}px`
  }

  // ─── Keyboard shortcuts ───────────────────────────────────────────────────

  _onKeyDown(event) {
    if (event.target.matches("input, textarea, select, [contenteditable]"))
      return
    if (event.metaKey || event.ctrlKey || event.altKey) return

    switch (event.key) {
      case "m":
        this._switchTo("month")
        break
      case "w":
        this._switchTo("week")
        break
      case "d":
        this._switchTo("day")
        break
      case "t":
        this.today()
        event.preventDefault()
        break
      case "ArrowLeft":
        this.previousPeriod()
        event.preventDefault()
        break
      case "ArrowRight":
        this.nextPeriod()
        event.preventDefault()
        break
    }
  }

  _switchTo(view) {
    this.viewValue = view
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("view", view)
    this._visitFrame(url.toString())
  }
}
